import { Capability, CAPABILITY_CATALOGUE } from "@/types/Capability";

export {
  CAPABILITY_CATALOGUE,
  expandImplied,
  missingFieldPrerequisites,
  validateCapabilitySet,
} from "@/types/Capability";
export type { Capability, CapabilityDescriptor, CapabilityGroup, CapabilityWarning } from "@/types/Capability";

/**
 * Client-side mirror of the backend `Capability.IMPLICIT` map. Lets the UI
 * keep `can(...)` working even if the backend hasn't populated
 * `EventRoleDTO.capabilities` (older response, cache miss, dev environment).
 *
 * MUST stay in sync with `org.nikita.app.authorization.Capability` —
 * adding/removing an implicit cap in only one place is the most likely
 * source of UI/server drift bugs.
 */
const VIEWER_CAPS: Capability[] = [
  "guests:read", "guests:read.companions", "guests:read.duplicates",
  "dashboards:read",
  "event-roles:read",
  "event-role-templates:read", "event-role-tokens:read",
  "additional-fields:read", "event:settings:read",
  // P4 reads
  "workflows:read",
  "email-templates:read", "sent-emails:read",
  "sender-addresses:read", "email-domains:read",
  "website:read",
  "slotting:read",
  "seating:read",
  "check-in-devices:read", "check-in-rules:read", "check-in-logs:read",
  "attachments:read", "shared-links:read",
  "documents:read",
  "event-program:read", "event-speakers:read", "event-sponsors:read",
  "event-goals:read",
  "event-setup:read",
];

const EDITOR_CAPS: Capability[] = [
  ...VIEWER_CAPS,
  "guests:read.qr-token",
  "guests:write", "guests:check-in", "guests:export",
  "additional-fields:write",
  "dashboards:write",
  // P4 writes
  "workflows:write",
  "email-templates:write", "sent-emails:send",
  "website:write",
  "slotting:write",
  "seating:write",
  "attachments:write", "shared-links:write", "shared-links:revoke",
  "documents:write", "documents:export",
  "event-program:write", "event-speakers:write", "event-sponsors:write",
  "check-in-devices:write", "check-in-rules:write",
  // event-goals:write intentionally NOT here: EDITOR (and VIEWER) may read
  // goals/KPIs but not edit them — editing is ADMIN/OWNER. Mirrors backend
  // Capability.EDITOR_CAPS.
  "event-setup:write",
  "ai:use",
];

const ADMIN_CAPS: Capability[] = [
  ...EDITOR_CAPS,
  "guests:delete",
  "event-roles:write",
  "event-role-templates:write", "event-role-tokens:write",
  "additional-fields:delete",
  "event:settings:write",
  // P4 deletes + admin-only writes
  "workflows:delete",
  "email-templates:delete",
  "slotting:delete",
  "seating:delete",
  "check-in-devices:delete",
  "attachments:delete",
  "documents:delete",
  "event-program:delete", "event-speakers:delete", "event-sponsors:delete",
  "event-goals:write",
  // The event's slice of the company audit trail (activity dialog, audit tab).
  "event-audit:read",
];

export const IMPLICIT_CAPS: Record<string, Set<Capability>> = {
  OWNER: new Set<Capability>([...ADMIN_CAPS, "owner-only:transfer"]),
  ADMIN: new Set<Capability>(ADMIN_CAPS),
  EDITOR: new Set<Capability>(EDITOR_CAPS),
  VIEWER: new Set<Capability>(VIEWER_CAPS),
};

/**
 * Compute the synthetic legacy floor for a CUSTOM template's capabilities.
 * Mirrors `Capability.computeLegacyFloor` on the backend.
 *
 * Scanner-template-aware rule: any active state-change cap → EDITOR.
 */
export function computeLegacyFloor(
  capabilities: ReadonlySet<Capability>,
): "EDITOR" | "VIEWER" {
  if (
    capabilities.has("guests:write") ||
    capabilities.has("guests:check-in") ||
    capabilities.has("guests:delete") ||
    capabilities.has("workflows:write") ||
    capabilities.has("email-templates:write") ||
    capabilities.has("sent-emails:send") ||
    capabilities.has("website:write") ||
    capabilities.has("slotting:write") ||
    capabilities.has("seating:write") ||
    capabilities.has("attachments:write") ||
    capabilities.has("shared-links:write") ||
    capabilities.has("shared-links:revoke") ||
    capabilities.has("documents:write") ||
    capabilities.has("documents:export") ||
    capabilities.has("check-in-devices:write") ||
    capabilities.has("check-in-rules:write") ||
    capabilities.has("additional-fields:write") ||
    capabilities.has("event:settings:write") ||
    capabilities.has("dashboards:write")
  ) {
    return "EDITOR";
  }
  return "VIEWER";
}

/**
 * Convenience: resolve a capability set from an EventRoleDTO. Prefers the
 * server-computed list; falls back to the IMPLICIT_CAPS table for non-CUSTOM
 * roles when the response doesn't carry capabilities.
 */
export function resolveCapabilities(args: {
  role: string;
  capabilities?: Capability[] | null;
}): ReadonlySet<Capability> {
  if (args.capabilities && args.capabilities.length > 0) {
    return new Set<Capability>(args.capabilities);
  }
  return IMPLICIT_CAPS[args.role] ?? new Set<Capability>();
}

/**
 * Client mirror of the backend `Capability.READ_ONLY_CAPS`
 * (`org.nikita.app.authorization.Capability`). A cap is read-only unless its
 * verb mutates state. MUST stay equivalent to the backend rule — the snapshot
 * test in `__tests__/capabilities.readOnly.test.ts` pins it so it can't drift.
 *
 * Why the FE needs it: live capability editing in the role-preview bar only
 * RE-MINTS the preview token when the *read* subset changes (write caps are
 * always masked off server-side and only affect chrome). This isolates that
 * read subset.
 */
function isReadOnlyCap(c: Capability): boolean {
  // owner-only:transfer is an ownership mutation; ai:use is self-escalation —
  // both excluded from preview tokens even though they have no mutating `:verb`.
  if (c === "owner-only:transfer" || c === "ai:use") return false;
  return !(
    c.includes(":write") ||
    c.includes(":delete") ||
    c.includes(":check-in") ||
    c.includes(":send") ||
    c.includes(":export") ||
    c.includes(":revoke") ||
    c.includes(":apply")
  );
}

export const READ_ONLY_CAPS: ReadonlySet<Capability> = new Set(
  CAPABILITY_CATALOGUE.map((d) => d.key).filter(isReadOnlyCap),
);

/**
 * The sorted read-only subset of a capability set — the exact payload a preview
 * re-mint sends to the backend, and the value compared to decide whether a
 * re-mint is even needed (write-cap-only edits never change it → no network).
 */
export function readOnlySubset(caps: Iterable<Capability>): Capability[] {
  const out: Capability[] = [];
  for (const c of caps) if (READ_ONLY_CAPS.has(c)) out.push(c);
  return out.sort();
}

/**
 * Whether the caller can submit a background job
 * (POST /api/v1/background-jobs/{eventId}).
 *
 * main-backend's `BackgroundJobResource.submitJob` is @RolesAllowed for the
 * implicit roles only — EVENT-CUSTOM is not on the list (EVENT-VIEWER isn't
 * either, and lacks `guests:write` anyway) — so a CUSTOM role's submit ends in
 * a 403 whatever its capabilities say. A surface whose action IS a job must not
 * offer it to such a role: the confirm is reachable, the answer is a refusal.
 */
export function canSubmitBackgroundJobs(caps: { isCustom: boolean }): boolean {
  return !caps.isCustom;
}
